import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, CircleAlert, FileText, Info } from "lucide-react";
import {
  agenticTaskModeOptions,
  normalizeAgenticTaskMode,
  type AgenticTaskMode,
} from "../../../lib/agentic/task-mode";
import {
  getPermissionLevelOption,
  permissionLevelOptions,
  type PermissionLevel,
} from "../../../lib/permission-levels";

interface AgenticModeToggleProps {
  value: AgenticTaskMode;
  onChange: (value: AgenticTaskMode) => void;
  compact?: boolean;
  permissionLevel?: PermissionLevel;
  onPermissionLevelChange?: (value: PermissionLevel) => void;
}

export function AgenticModeToggle({
  value,
  onChange,
  compact,
  permissionLevel,
  onPermissionLevelChange,
}: AgenticModeToggleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [permissionOpen, setPermissionOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const normalizedValue = normalizeAgenticTaskMode(value);
  const permission = getPermissionLevelOption(permissionLevel);
  const showPermissionPicker = Boolean(
    permissionLevel && onPermissionLevelChange,
  );
  const active =
    agenticTaskModeOptions.find((option) => option.value === normalizedValue) ??
    agenticTaskModeOptions[0];

  const close = () => {
    setPermissionOpen(false);
    setIsClosing(true);
    window.setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 140);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={`${active.label}: ${active.title}`}
        className={[
          "fintheon-agentic-mode-trigger flex h-8 items-center justify-center gap-1.5 rounded-lg px-1.5 text-[10px] font-semibold transition-colors",
          compact ? "min-w-0" : "min-w-0",
        ].join(" ")}
        style={{
          color: active.triggerText,
        }}
      >
        <span>{active.label}</span>
        <ChevronDown size={10} className="shrink-0 opacity-55" />
      </button>

      {isOpen ? (
        <div
          role="menu"
          aria-label="Agentic mode"
          className={[
            "fintheon-agentic-mode-menu fintheon-popover-motion absolute bottom-full left-0 z-50 mb-2 overflow-hidden rounded-lg border border-[var(--fintheon-accent)]/15 bg-[#0a0905]",
            compact ? "w-[220px]" : "w-[260px]",
            isClosing ? "is-closing" : "",
          ].join(" ")}
          style={{ boxShadow: "none" }}
        >
          <div className="border-b border-[var(--fintheon-accent)]/10 px-3 py-2">
            <p className="text-[10px] font-semibold text-[var(--fintheon-accent)]">
              Mode
            </p>
          </div>
          <div>
            {agenticTaskModeOptions.map((option) => {
              const isActive = normalizedValue === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isActive}
                  onClick={() => {
                    onChange(option.value);
                    close();
                  }}
                  className="flex w-full items-start gap-2 px-3 py-2 text-left transition-colors hover:bg-white/[0.04]"
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-medium text-[var(--fintheon-text)]/82">
                        {option.label}
                      </span>
                      {isActive ? (
                        <Check
                          size={10}
                          className="shrink-0 text-[var(--fintheon-text)]/45"
                        />
                      ) : null}
                    </span>
                    <span className="mt-0.5 block text-[10px] leading-4 text-[var(--fintheon-text)]/38">
                      {option.title}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          {showPermissionPicker ? (
            <div
              className="fintheon-agentic-permission-drill"
              data-open={permissionOpen ? "true" : "false"}
            >
              <div
                className="fintheon-agentic-permission-options-shell"
                aria-hidden={!permissionOpen}
              >
                <div
                  className="fintheon-agentic-permission-options t-panel-slide"
                  data-open={permissionOpen ? "true" : "false"}
                >
                  {permissionLevelOptions.map((option) => {
                    const isActive = option.value === permission.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="menuitemradio"
                        aria-checked={isActive}
                        tabIndex={permissionOpen ? 0 : -1}
                        onClick={() => {
                          onPermissionLevelChange?.(option.value);
                          setPermissionOpen(false);
                        }}
                        className="flex h-8 w-full items-center justify-between gap-2 px-2 text-left transition-colors hover:bg-white/[0.04]"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <PermissionLevelIcon level={option.value} />
                          <span className="truncate text-[11px] font-medium text-[var(--fintheon-text)]/80">
                            {option.label}
                          </span>
                        </span>
                        {isActive ? (
                          <Check
                            size={10}
                            className="shrink-0 text-[var(--fintheon-text)]/45"
                          />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                type="button"
                aria-expanded={permissionOpen}
                onClick={() => setPermissionOpen((current) => !current)}
                className="fintheon-agentic-permission-trigger"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <PermissionLevelIcon level={permission.value} />
                  <span className="truncate text-[11px] font-medium text-[var(--fintheon-text)]/82">
                    {permission.label}
                  </span>
                </span>
                <ChevronDown
                  size={11}
                  className={`shrink-0 text-[var(--fintheon-text)]/45 transition-transform duration-150 ${
                    permissionOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function PermissionLevelIcon({ level }: { level: PermissionLevel }) {
  if (level === "full") {
    return (
      <CircleAlert
        size={13}
        className="shrink-0 text-[var(--fintheon-accent)]"
      />
    );
  }
  if (level === "limited") {
    return (
      <Info size={13} className="shrink-0 text-[var(--fintheon-secondary)]" />
    );
  }
  return (
    <FileText size={13} className="shrink-0 text-[var(--fintheon-text)]/58" />
  );
}
